import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getRolesAPI,
  getUserDetailAPI,
  getUserListAPI,
  patchActivateAccountAPI,
  patchDeactivateAccountAPI,
  postUserAPI,
  putUserAPI,
} from 'features/Account/services/intoolsAccess/user'
import { serializeQueryToURL } from 'utils/queryParamsURL'
import { toastSuccess } from 'utils/toast'
import { getLocationDetail } from 'features/Location/services/location'
import type { AppDispatchType } from 'store'

export const SLICE_NAME = 'INTOOLS_ACCESS/user'

export const fetchGetLocationSelected = createAsyncThunk(
  `${SLICE_NAME}/fetchGetLocationSelected`,
  async (locationIds: number[], { rejectWithValue }) => {
    try {
      const locationPromises = locationIds.map((locationId) => getLocationDetail(locationId))
      const res = await Promise.all(locationPromises)

      return res.map(({ data }) => ({
        location_id: data.location_id,
        location_name: data.location_name,
        location_type: data.location_type,
      }))
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetUserList = createAsyncThunk(
  `${SLICE_NAME}/fetchGetUserList`,
  async ({ params }: { params: Parameters<typeof getUserListAPI>[0] }, { rejectWithValue }) => {
    try {
      const res = await getUserListAPI(params)

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetUserDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetUserDetail`,
  async (
    { userId }: { userId: Parameters<typeof getUserDetailAPI>[0] },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await getUserDetailAPI(userId)

      if (res.data.data.roles.length) {
        const locationIds = res.data.data.roles[0].location_ids
        dispatch(fetchGetLocationSelected(locationIds))
      }

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const actGetUserList =
  ({ params }: { params?: Parameters<typeof getUserListAPI>[0] }) =>
  (dispatch: StoreDispatchType) => {
    const qs = serializeQueryToURL({ page_index: 0, ...params })
    dispatch(fetchGetUserList({ params: qs }))
  }

export const fetchPostUser = createAsyncThunk(
  `${SLICE_NAME}/fetchPostUser`,
  async (
    { callback }: { callback: (() => void) | undefined },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const { userIntoolsAccess } = getState() as StoreStateType
      const { form } = userIntoolsAccess

      const payload: Parameters<typeof postUserAPI>[0] = {
        name: form.name,
        email: form.email,
        phone_number: form.phoneNumber,
        position: form.position,
        roles: form.roles.map(({ id }) => ({ id })),
        is_super_admin: false,
      }

      const res = await postUserAPI(payload)

      if (callback) {
        callback()
      }

      ;(dispatch as AppDispatchType)(actGetUserList({}))

      toastSuccess(`User ${form.name} berhasil ditambahkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPutUser = createAsyncThunk(
  `${SLICE_NAME}/fetchPutUser`,
  async (
    { callback }: { callback: (() => void) | undefined },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const { userIntoolsAccess } = getState() as StoreStateType
      const {
        form,
        feature: {
          drawerAddAccount: { data },
        },
      } = userIntoolsAccess

      const mapIdsToStatus = (
        currentIds: number[],
        selectedItems: number[],
      ): {
        id: number
        is_deleted: boolean
      }[] => {
        const selectedIds = new Set(selectedItems)

        return Array.from(new Set([...currentIds, ...selectedItems])).map((id) => ({
          id,
          is_deleted: !selectedIds.has(id),
        }))
      }

      const payload: Parameters<typeof putUserAPI>[0]['payload'] = {
        name: form.name,
        email: form.email,
        phone_number: form.phoneNumber,
        position: form.position,
        roles: mapIdsToStatus(
          data?.roles.map((r) => r.id) ?? [],
          form.roles.map((r) => r.id),
        ),
        is_super_admin: false,
      }

      const res = await putUserAPI({
        id: form.id as number,
        payload,
      })

      if (callback) {
        callback()
      }

      ;(dispatch as AppDispatchType)(actGetUserList({}))

      toastSuccess(`User ${form.name} berhasil diubah`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPatchActivateAccount = createAsyncThunk(
  `${SLICE_NAME}/fetchPatchActivateAccount`,
  async (
    {
      params,
    }: {
      params: {
        userId: number
        name: string
      }
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { userId, name } = params
      const res = await patchActivateAccountAPI(userId)

      ;(dispatch as AppDispatchType)(actGetUserList({}))
      toastSuccess(`User ${name} berhasil diaktifkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPatchDeactivateAccount = createAsyncThunk(
  `${SLICE_NAME}/fetchPatchDeactivateAccount`,
  async (
    payload: {
      params: {
        userId: number
        name: string
      }
      callback?: () => void
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { params, callback } = payload
      const { userId, name } = params
      const res = await patchDeactivateAccountAPI(userId)

      ;(dispatch as AppDispatchType)(actGetUserList({}))
      if (callback) {
        callback()
      }
      toastSuccess(`User ${name} berhasil dinon-aktifkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetRoles = createAsyncThunk(
  `${SLICE_NAME}/fetchGetRoles`,
  async ({ params }: { params?: Parameters<typeof getRolesAPI>[0] }, { rejectWithValue }) => {
    try {
      const res = await getRolesAPI(params)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
